import React, { useEffect, useState } from 'react';
import { app, authentication } from '@microsoft/teams-js';
import {
    Dropdown,
    Textarea,
    Button,
    Option,
    Label,
    Spinner,
} from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { submitDialog } from '../../utils/teams';
import { IBotSubmitPayload } from '../../model/IBotSubmitPayload';
import BotSubmitAction from '../../model/BotSubmitAction';
import { useRateConversationPageStyles } from './RateConversationPage.styles';
import { useSearchParams } from 'react-router';

const RateConversationPage = () => {
    const { t } = useTranslation();
    const styles = useRateConversationPageStyles();
    const [searchParams] = useSearchParams();

    const brokerConnectionId = searchParams.get('brokerConnectionId');
    const rating = searchParams.get('rating');
    const redirectToNewConversation =
        searchParams.get('redirectToNewConversation') === 'true';

    const [selectedRating, setSelectedRating] = useState<string>(rating || '');
    const [comment, setComment] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        app.notifySuccess();
    }, []);

    const showRatingDropdown = redirectToNewConversation || !rating;
    const currentRating = selectedRating || rating;
    const isCommentRequired = currentRating
        ? parseInt(currentRating) <= 3
        : false;
    const isSubmitDisabled =
        isSubmitting ||
        (showRatingDropdown && !selectedRating) ||
        (isCommentRequired && !comment.trim());

    const handleSubmit = async () => {
        if (isSubmitting) return;

        if (isCommentRequired && !comment.trim()) {
            return;
        }

        if (showRatingDropdown && !selectedRating) {
            return;
        }

        try {
            setIsSubmitting(true);
            const token = await authentication.getAuthToken();

            const submitRequest: IBotSubmitPayload<any> = {
                data: {
                    BrokerConnectionId: brokerConnectionId,
                    rating: selectedRating || rating,
                    ratingComment: comment.trim() || null,
                    RedirectToNewConversation: redirectToNewConversation,
                    SkipRating: false,
                },
                appUserAccessToken: token,
                action: BotSubmitAction.RateConversation,
            };
            submitDialog(submitRequest);
        } catch (error) {
            console.error('Error submitting rating:', error);
            setIsSubmitting(false);
        }
    };

    const handleProceedWithoutRating = async () => {
        if (isSubmitting) return;

        try {
            setIsSubmitting(true);
            const token = await authentication.getAuthToken();

            const submitRequest: IBotSubmitPayload<any> = {
                data: {
                    BrokerConnectionId: brokerConnectionId,
                    rating: null,
                    ratingComment: null,
                    RedirectToNewConversation: true,
                    SkipRating: true,
                },
                appUserAccessToken: token,
                action: BotSubmitAction.RateConversation,
            };
            submitDialog(submitRequest);
        } catch (error) {
            console.error('Error proceeding without rating:', error);
            setIsSubmitting(false);
        }
    };

    return (
        <div className={styles.root}>
            {showRatingDropdown && (
                <div className={styles.formField}>
                    <Label weight="semibold" required>
                        {t('Table_Header_Ratings_Rating')}
                    </Label>
                    <Dropdown
                        placeholder={t('ResolveConversation_RatingPlaceholder')}
                        value={
                            selectedRating
                                ? `${selectedRating} - star${parseInt(selectedRating) > 1 ? 's' : ''}`
                                : ''
                        }
                        selectedOptions={selectedRating ? [selectedRating] : []}
                        onOptionSelect={(_, data) =>
                            setSelectedRating(data.optionValue as string)
                        }
                    >
                        <Option value="5">
                            {t('ResolveConversation_ExcellentRating')}
                        </Option>
                        <Option value="4">
                            {t('ResolveConversation_GoodRating')}
                        </Option>
                        <Option value="3">
                            {t('ResolveConversation_AverageRating')}
                        </Option>
                        <Option value="2">
                            {t('ResolveConversation_PoorRating')}
                        </Option>
                        <Option value="1">
                            {t('ResolveConversation_VeryPoorRating')}
                        </Option>
                    </Dropdown>
                </div>
            )}
            <div className={styles.formField}>
                <Label weight="semibold" required={isCommentRequired}>
                    {isCommentRequired
                        ? t('RateConversation_CommentsRequired')
                        : 'Comments (Optional)'}
                </Label>
                <Textarea
                    rows={4}
                    className={styles.textArea}
                    value={comment}
                    maxLength={1000}
                    placeholder={t('RateConversation_CommentsPlaceholder')}
                    onChange={(_, data) => setComment(data?.value || '')}
                    required
                />
                <div className={styles.helperText}>
                    {t('RateConversation_CommentsHelper')}
                </div>
            </div>

            <div className={styles.actions}>
                {redirectToNewConversation && (
                    <Button
                        iconPosition="before"
                        icon={
                            isSubmitting ? <Spinner size="tiny" /> : undefined
                        }
                        appearance="subtle"
                        disabled={isSubmitting}
                        onClick={handleProceedWithoutRating}
                    >
                        {t('RateConversation_SkipRating')}
                    </Button>
                )}
                <Button
                    iconPosition="before"
                    icon={isSubmitting ? <Spinner size="tiny" /> : undefined}
                    appearance="primary"
                    disabled={isSubmitDisabled}
                    onClick={handleSubmit}
                    style={{ marginLeft: '8px' }}
                >
                    {t('RateConversation_Submit')}
                </Button>
            </div>
        </div>
    );
};

export default RateConversationPage;
